import {Dictionary, noop} from 'lodash';

import {globalDocument, globalWindow} from '../../libs/shared/helpers/src/browser/browserHelpers';
import {
  addQueryParamsToPathAndQuery,
  authenticateUrlForWindow,
} from '../../libs/shared/helpers/src/browser/urlHelpers';
import {electronAppVersionGTE, isElectron} from '../../libs/shared/helpers/src/environment/platformHelpers';
import {HandlerOf} from '../../libs/shared/helpers/src/react/reactHelpers';
import {ErrorWithData} from '../../libs/shared/helpers/src/types/customErrors';
import {randomString} from '../../libs/shared/helpers/src/utilities/stringHelpers';
import {openInBrowser} from '../../srcCommon/browser/openInBrowser';
import {CancelError, CancelToken} from '../helpers/asyncCancelHelpers';
import {delayAsync} from '../helpers/asyncHelpers';
import {downloadFileFromUrl} from '../helpers/fileHelpers';
import {UploadedAttachmentModel} from '../models/api/attachmentModel';
import {
  cancelDownload,
  downloadItem,
  ElectronDownloadItemTypesEnum,
  focusMainWindow,
} from './electron/electronBridgeOutbound';
import {httpToken} from './http/httpBase';

/*
 * Downloads state.
 */

interface Download {
  url: string;
  onProgress: HandlerOf<number>;
  onSuccess: HandlerOf<string>;
  onError: HandlerOf<Error>;
  createdAt: number;
}

const downloads = new Map<string, Download>();

interface TrackDownloadOptions {
  onProgress?: HandlerOf<number>;
  cancelToken?: CancelToken;
}

function wrapTrackDownloadAsync(
  url: string,
  {onProgress, cancelToken}: TrackDownloadOptions,
  startDownload: HandlerOf<string>,
) {
  return new Promise<string>((resolve, reject) => {
    if (url.startsWith('data:')) {
      startDownload(url);
      resolve('');
      return;
    }

    // Uniquely identify this download.
    const downloadId = randomString();
    const uniqueUrl = addQueryParamsToPathAndQuery(url, {download_id: downloadId});

    // Clear our transient download state after a delay.
    const cleanupTimeout = globalWindow.setInstrumentedTimeout(() => cleanup(), 2 * 60 * 60 * 1000); // Cleanup after 2h.
    const cleanup = () => {
      globalWindow.clearTimeout(cleanupTimeout);
      downloads.delete(uniqueUrl);
    };

    // Event handlers.
    const onSuccess: HandlerOf<string> = (filepath) => {
      cleanup();
      resolve(filepath);
    };

    const onError: HandlerOf<Error> = (error) => {
      cleanup();
      reject(error);
    };

    // Keep track of this download.
    downloads.set(uniqueUrl, {
      url: uniqueUrl,
      onProgress: onProgress || noop,
      onSuccess,
      onError,
      createdAt: Date.now(),
    });

    // Handle cancellation.
    if (cancelToken) {
      cancelToken.promise.then(() => {
        if (!downloads.has(uniqueUrl)) {
          return;
        }

        cleanup();
        cancelDownload(uniqueUrl);
        reject(new CancelError());
      });
    }

    // Start the download with the new URL we created.
    startDownload(uniqueUrl);
  });
}

export enum DownloadStatusesEnum {
  IN_PROGRESS = 'IN_PROGRESS',
  FAILED = 'FAILED',
  SUCCEEDED = 'COMPLETED',
}

interface ElectronDownloadInProgressEvent {
  status: DownloadStatusesEnum.IN_PROGRESS;
  progress: number;
}

interface ElectronDownloadFailedEvent {
  status: DownloadStatusesEnum.FAILED;
}

interface ElectronDownloadSucceededEvent {
  status: DownloadStatusesEnum.SUCCEEDED;
  filepath: string;
}

type ElectronDownloadEvent =
  | ElectronDownloadInProgressEvent
  | ElectronDownloadFailedEvent
  | ElectronDownloadSucceededEvent;

export function onElectronDownloadEvent(url: string, event: ElectronDownloadEvent) {
  // Find the state for this download.
  const download = downloads.get(url);

  // The download may have been cancelled but had pending progress events, just skip.
  if (!download) {
    return;
  }

  if (event.status === DownloadStatusesEnum.IN_PROGRESS) {
    download.onProgress(event.progress);
    return;
  }

  if (event.status === DownloadStatusesEnum.SUCCEEDED) {
    download.onSuccess(event.filepath);
    return;
  }

  if (event.status === DownloadStatusesEnum.FAILED) {
    const error = new ErrorWithData('[Download] Download a file failed.', {url});
    download.onError(error);
  }
}

/*
 * Downloads API.
 */

function openUrl(url: string) {
  // Note: We need to specify the 'noopener' feature for PDF previews since they can redirect to other
  // pages which would otherwise be able to redirect the original app tab. See: PB-18818
  globalWindow.open(url, '_blank', 'noopener');
}

export async function previewFileAsync(url: string, name: string, options: TrackDownloadOptions = {}) {
  // Web.
  if (!isElectron()) {
    const match = url.match(/^data:(.*;base64),(.*)$/);
    if (match) {
      // Long "data:" URLs need to be converted into blobs in order to be previewed
      const byteString = atob(match[2]);
      const byteArray = new Uint8Array(new ArrayBuffer(byteString.length));
      for (let i = 0; i < byteString.length; i++) {
        byteArray[i] = byteString.charCodeAt(i);
      }
      const file = new File([byteArray], name, {type: match[1]});
      const fileUrl = URL.createObjectURL(file);
      try {
        openUrl(fileUrl);
      } finally {
        URL.revokeObjectURL(fileUrl);
      }
      return;
    }

    const attachmentUrl = authenticateGlobalUrl(url, {action: 'view'});
    openUrl(attachmentUrl);
    return;
  }

  await wrapTrackDownloadAsync(url, options, (uniqueUrl) => {
    // Desktop app, standalone.
    downloadItem(uniqueUrl, ElectronDownloadItemTypesEnum.PREVIEW, name);
  });
}

interface DownloadFileResult {
  filepath?: string;
}

interface DownloadFileOptions extends TrackDownloadOptions {
  shouldPromptForPath?: boolean;
}

export async function downloadFileAsync(
  url: string,
  name: string,
  options: DownloadFileOptions = {},
): Promise<DownloadFileResult> {
  const {onProgress, shouldPromptForPath, cancelToken} = options;

  // Web.
  if (!isElectron()) {
    const attachmentUrl = url.startsWith('data:') ? url : authenticateGlobalUrl(url);
    downloadFileFromUrl(globalDocument, attachmentUrl, name);

    if (!onProgress) {
      return {};
    }

    // Fake progress for better UI results.
    await delayAsync(500, cancelToken);
    onProgress(0.5);
    await delayAsync(500, cancelToken);
    onProgress(1);

    return {};
  }

  const filepath = await wrapTrackDownloadAsync(url, options, (uniqueUrl) => {
    // Desktop app, standalone.
    downloadItem(uniqueUrl, ElectronDownloadItemTypesEnum.DOWNLOAD, name, shouldPromptForPath);
  });

  return {filepath};
}

/*
 * Attachments API.
 */

export function previewAttachmentAsync(
  attachment: UploadedAttachmentModel,
  options: TrackDownloadOptions = {},
) {
  return previewFileAsync(attachment.url, attachment.name, options);
}

export type PreviewAttachmentOptions = TrackDownloadOptions;
export type AttachmentPreviewer = typeof previewAttachmentAsync;

export function downloadAttachmentAsync(
  attachment: UploadedAttachmentModel,
  options: DownloadFileOptions = {},
) {
  return downloadFileAsync(attachment.url, attachment.name, options);
}

export type DownloadAttachmentOptions = DownloadFileOptions;
export type DownloadAttachmentResult = DownloadFileResult;
export type AttachmentDownloader = typeof downloadAttachmentAsync;

/*
 * API.
 */

export function reloadBrowser() {
  globalWindow.location.reload();
}

/** Open the URL for a topic in a browser. */
export function openTopicLinkInBrowser(url: string) {
  // A topic can be any string.
  // If the protocol is missing we add it.
  if (!/^http(s?):\/\//.test(url)) {
    openInBrowser(`https://${url}`);
    return;
  }

  openInBrowser(url);
}

export function tryFocusMainWindow() {
  if (!isElectron() || !electronAppVersionGTE('3.9.11')) {
    return;
  }

  focusMainWindow();
}

export function redirectToSignin() {
  globalWindow.location.href = '/signin';
}

/*
 * Helpers.
 */

function authenticateGlobalUrl(url: string, options: Dictionary<string> = {}) {
  return authenticateUrlForWindow(globalWindow, url, {...options, access_token: httpToken()});
}
